import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { message } from 'antd';

import type { ImportImageUrlFn, UploadImageFn, UploadedImage } from './types';

const PROGRESS_MESSAGE_KEY = 'rte-clipboard-images';

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
	'image/png': 'png',
	'image/jpeg': 'jpg',
	'image/gif': 'gif',
	'image/webp': 'webp',
};

export interface ClipboardImageAdoptionOptions {
	/** `data-*` attribute marking an image as one we already store. */
	dataAttr: string;
	/**
	 * Read through getters rather than captured: the extension is built once
	 * per editor, while the handlers usually close over a record id that only
	 * exists later.
	 */
	getUpload: () => UploadImageFn | undefined;
	getImportUrl: () => ImportImageUrlFn | undefined;
}

/**
 * Takes ownership of images that arrive as markup instead of as bytes.
 *
 * Copying an image out of most applications puts a file on the clipboard, which
 * `FileHandler` uploads. Google Docs, Confluence and ordinary web pages do not:
 * they offer `text/html` alone, with each `<img>` still pointing at their own
 * host. Pasted as-is those tags either name a `data:` or `file:///` source the
 * server refuses to store, or hot-link a URL that expires — so the image is
 * gone by the time anyone reads the note.
 *
 * This intercepts such a paste, turns every foreign `<img>` into a stored one,
 * and inserts the rewritten markup.
 */
export function createClipboardImageAdoption(
	options: ClipboardImageAdoptionOptions
) {
	const { dataAttr, getUpload, getImportUrl } = options;

	/** Stores one clipboard `src`, or resolves null if it cannot be recovered. */
	const adopt = async (src: string): Promise<UploadedImage | null> => {
		try {
			if (src.startsWith('data:') || src.startsWith('blob:')) {
				const upload = getUpload();
				if (!upload) return null;
				const file = await fileFromLocalSrc(src);
				return file ? await upload(file) : null;
			}
			if (/^https?:/i.test(src)) {
				// Reading another origin's bytes needs its permission, which it
				// rarely grants, so this one goes through the server.
				const importUrl = getImportUrl();
				return importUrl ? await importUrl(src) : null;
			}
			// `file:///`, `cid:` and friends: the bytes never left the source
			// application, and nothing we do here can reach them.
			return null;
		} catch {
			return null;
		}
	};

	const adoptAllAndInsert = async (
		editor: Editor,
		doc: Document,
		foreign: HTMLImageElement[]
	) => {
		message.open({
			key: PROGRESS_MESSAGE_KEY,
			type: 'loading',
			content:
				foreign.length === 1
					? 'Saving pasted image...'
					: `Saving ${foreign.length} pasted images...`,
			duration: 0,
		});

		// The same picture can appear twice in one paste; store it once.
		const adoptedBySrc = new Map<string, Promise<UploadedImage | null>>();
		foreign.forEach((image) => {
			const src = image.getAttribute('src') as string;
			if (!adoptedBySrc.has(src)) adoptedBySrc.set(src, adopt(src));
		});

		let lost = 0;
		await Promise.all(
			foreign.map(async (image) => {
				const src = image.getAttribute('src') as string;
				const adopted = await adoptedBySrc.get(src);
				if (!adopted) {
					// Better an obviously missing image than one that renders
					// today and 404s next week.
					image.remove();
					lost += 1;
					return;
				}
				image.setAttribute('src', adopted.url);
				image.setAttribute(dataAttr, adopted.id);
			})
		);

		message.destroy(PROGRESS_MESSAGE_KEY);
		if (lost > 0) {
			message.error(
				lost === 1
					? 'One pasted image could not be saved.'
					: `${lost} pasted images could not be saved.`
			);
		}
		editor.commands.insertContent(doc.body.innerHTML);
	};

	return Extension.create({
		name: 'clipboardImageAdoption',

		addProseMirrorPlugins() {
			const { editor } = this;

			return [
				new Plugin({
					key: new PluginKey('clipboardImageAdoption'),
					props: {
						handlePaste: (_view, event) => {
							const clipboard = event.clipboardData;
							if (!clipboard || hasImageFile(clipboard)) return false;

							const html = clipboard.getData('text/html');
							if (!html || !html.includes('<img')) return false;

							const doc = new DOMParser().parseFromString(html, 'text/html');
							const foreign = Array.from(doc.querySelectorAll('img')).filter(
								(image) =>
									!!image.getAttribute('src') && !image.getAttribute(dataAttr)
							);
							if (foreign.length === 0) return false;

							event.preventDefault();
							void adoptAllAndInsert(editor, doc, foreign);
							return true;
						},
					},
				}),
			];
		},
	});
}

/** True when the clipboard carries the bytes, which `FileHandler` deals with. */
function hasImageFile(clipboard: DataTransfer): boolean {
	return Array.from(clipboard.items).some(
		(item) => item.kind === 'file' && item.type.startsWith('image/')
	);
}

/** Reads a `data:` or `blob:` source, both of which the page may fetch. */
async function fileFromLocalSrc(src: string): Promise<File | null> {
	try {
		const blob = await (await fetch(src)).blob();
		if (!blob.type.startsWith('image/')) return null;
		const extension = EXTENSION_BY_MIME_TYPE[blob.type] || 'png';
		return new File([blob], `pasted-image.${extension}`, { type: blob.type });
	} catch {
		return null;
	}
}
